import type { LatLng } from '@/types/global';
import type {
  ChargePointWrapper,
  ChargePointCluster,
} from '@/features/charge-points/types/charge-point.types';

/**
 * Threshold for considering two coordinates as overlapping (in degrees)
 * ~100 meters at equator (increased for better overlap detection at zoom 17)
 * This prevents visual overlap of markers on the map
 */
export const OVERLAP_THRESHOLD = 0.001;

/**
 * Create a unique key for a coordinate (rounded to threshold).
 *
 * Exported so other marker layers (e.g. charge-session markers) can collapse
 * coincident points onto the exact same grid as the charge-point markers.
 */
export function getCoordinateKey(coord: LatLng): string {
  const roundedLat =
    Math.round(coord.lat / OVERLAP_THRESHOLD) * OVERLAP_THRESHOLD;
  const roundedLng =
    Math.round(coord.lng / OVERLAP_THRESHOLD) * OVERLAP_THRESHOLD;
  return `${roundedLat.toFixed(6)},${roundedLng.toFixed(6)}`;
}

export interface MarkerWithOverlap {
  marker: ChargePointWrapper | ChargePointCluster;
  type: 'chargePoint' | 'cluster';
  overlapCount: number;
  overlapGroup: (ChargePointWrapper | ChargePointCluster)[];
}

/**
 * Detect overlapping markers and group them
 */
export function detectOverlappingMarkers(
  chargePoints: ChargePointWrapper[],
  clusters: ChargePointCluster[]
): {
  chargePoints: Map<string, MarkerWithOverlap>;
  clusters: Map<string, MarkerWithOverlap>;
  visibleChargePointIds: Set<string>;
  visibleClusterIds: Set<string>;
} {
  // Create map of all markers by coordinate key
  const coordinateGroups = new Map<
    string,
    (ChargePointWrapper | ChargePointCluster)[]
  >();

  // Group charge points
  chargePoints.forEach((cp) => {
    const key = getCoordinateKey(cp.coordinate);
    const group = coordinateGroups.get(key) || [];
    group.push(cp);
    coordinateGroups.set(key, group);
  });

  // Group clusters
  clusters.forEach((cluster) => {
    const key = getCoordinateKey(cluster.coordinate);
    const group = coordinateGroups.get(key) || [];
    group.push(cluster);
    coordinateGroups.set(key, group);
  });

  // Build result maps and track which markers should be visible
  const chargePointsMap = new Map<string, MarkerWithOverlap>();
  const clustersMap = new Map<string, MarkerWithOverlap>();
  const visibleChargePointIds = new Set<string>();
  const visibleClusterIds = new Set<string>();

  // Track which coordinate keys we've processed
  const processedKeys = new Set<string>();

  chargePoints.forEach((cp) => {
    const key = getCoordinateKey(cp.coordinate);
    const overlapGroup = coordinateGroups.get(key) || [cp];

    chargePointsMap.set(cp.id, {
      marker: cp,
      type: 'chargePoint',
      overlapCount: overlapGroup.length,
      overlapGroup,
    });

    // Only show first marker from each overlap group
    if (!processedKeys.has(key)) {
      visibleChargePointIds.add(cp.id);
      processedKeys.add(key);
    }
  });

  // Reset processed keys for clusters
  processedKeys.clear();

  clusters.forEach((cluster) => {
    const key = getCoordinateKey(cluster.coordinate);
    const overlapGroup = coordinateGroups.get(key) || [cluster];

    clustersMap.set(cluster.id, {
      marker: cluster,
      type: 'cluster',
      overlapCount: overlapGroup.length,
      overlapGroup,
    });

    // Only show first marker from each overlap group
    if (!processedKeys.has(key)) {
      visibleClusterIds.add(cluster.id);
      processedKeys.add(key);
    }
  });

  return {
    chargePoints: chargePointsMap,
    clusters: clustersMap,
    visibleChargePointIds,
    visibleClusterIds,
  };
}
